import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import type { LayoutDocument } from '../lib/form-layout/types';
import type { WellFormFieldName } from '../lib/form-layout/well-fields';

export type WellLayoutDocument = LayoutDocument<WellFormFieldName>;

export interface ResolvedLayout {
  layout: WellLayoutDocument;
  scope: 'global' | 'project' | 'default';
  resolved_from: 'project' | 'global' | 'none';
  source_id: string | null;
  updated_at: string | null;
  notes: string | null;
}

export const wellFormLayoutKeys = {
  all: ['well-form-layout'] as const,
  active: (projectId: string | null) => [...wellFormLayoutKeys.all, 'active', projectId] as const,
  adminActive: (scope: 'global' | 'project', projectId: string | null) =>
    [...wellFormLayoutKeys.all, 'admin', 'active', scope, projectId] as const,
};

export function useWellFormLayout(projectId: string | null) {
  return useQuery({
    queryKey: wellFormLayoutKeys.active(projectId),
    staleTime: 60_000,
    queryFn: () =>
      api.get<ResolvedLayout>('/well-form-layout', projectId ? { project_id: projectId } : undefined),
  });
}

export function useAdminActiveWellLayout(scope: 'global' | 'project', projectId: string | null) {
  return useQuery({
    queryKey: wellFormLayoutKeys.adminActive(scope, projectId),
    queryFn: () => {
      const q: Record<string, string> = { scope };
      if (scope === 'project' && projectId) q.project_id = projectId;
      return api.get<ResolvedLayout>('/admin/well-form-layout/active', q);
    },
    enabled: scope === 'global' || projectId !== null,
  });
}

export interface UpdateWellLayoutInput {
  scope: 'global' | 'project';
  project_id?: string | null;
  layout: WellLayoutDocument;
  notes?: string | null;
}

export function useUpdateWellFormLayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateWellLayoutInput) => api.put<ResolvedLayout>('/admin/well-form-layout', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: wellFormLayoutKeys.all }),
  });
}
